/**
 * /logistica/entregas/:id — Detalhe da OE com actions de transicao.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiCopy,
  FiRefreshCw,
  FiXCircle,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  conferirAssinaturaOe,
  copiarLinkAssinaturaOe,
  obterOe,
  registrarEntrega,
  registrarNaoEntrega,
  SITUACAO_OE_LABELS,
  SITUACAO_OE_TONES,
} from "../../../services/logistica";
import { PageContainer } from "../../../styles/shared";

import { fmtData } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SectionTitle,
  Textarea,
} from "../../Orcamentos/styles";


export default function EntregaDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [oe, setOe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [observacaoConferencia, setObservacaoConferencia] = useState("");
  const [linkPublico, setLinkPublico] = useState("");

  const podeEntregar = isMaster || hasAllPermissions(["logistica:entregar"]);
  // Quem planeja a logistica e quem recebe a pendencia da assinatura (CRM-38).
  const podePlanejar = isMaster || hasAllPermissions(["logistica:planejar"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await obterOe(request, id);
      setOe(data);
    } catch (err) {
      setError(err?.message || "Erro ao carregar OE.");
    } finally { setLoading(false); }
  }, [request, id]);

  useEffect(() => { carregar(); }, [carregar]);

  // O token da assinatura NAO vem no detalhe da OE desde o Security Sprint 2
  // (A12): ele sai sob demanda pela action dedicada, que exige
  // `logistica:entregar` e audita quem copiou. Montar a URL a partir de
  // `oe.assinatura_token` deixava o cartao inteiro invisivel — e o gestor
  // sem caminho nenhum para mandar ao cliente o link que ele precisa assinar.
  const copiarLink = async () => {
    const { url } = await copiarLinkAssinaturaOe(request, id);
    setLinkPublico(url);
    await navigator.clipboard?.writeText(url);
  };

  const executar = useCallback(async () => {
    if (!oe || !confirmando) return;
    let promise;
    if (confirmando === "entregar") promise = registrarEntrega(request, id);
    else if (confirmando === "nao_entregar") {
      if (!motivo.trim()) throw new Error("Motivo obrigatório.");
      promise = registrarNaoEntrega(request, id, { motivo });
    } else if (confirmando === "conferir") {
      promise = conferirAssinaturaOe(request, id, { observacao: observacaoConferencia });
    }
    if (promise) {
      const data = await promise;
      setOe(data);
      setConfirmando(null);
      setMotivo("");
      setObservacaoConferencia("");
    }
  }, [confirmando, motivo, observacaoConferencia, request, id, oe]);

  if (loading && !oe) return <PageContainer><EmptyMsg>Carregando...</EmptyMsg></PageContainer>;
  if (!oe) {
    return (
      <PageContainer>
        <EntityHeader title="Erro" actions={[{
          label: "Voltar", icon: <FiArrowLeft size={14} />,
          onClick: () => navigate("/logistica/entregas"),
        }]} />
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </PageContainer>
    );
  }

  // CRM-38: assinou quem nao esta entre os autorizados do cliente e ninguem
  // da logistica conferiu ainda. `null` = sem assinatura ou entrega antiga.
  const assinaturaAConferir = oe.assinante_autorizado === false && !oe.assinatura_conferida_em;
  const autorizados = oe.autorizados_assinatura || [];

  const actions = [
    { label: "Voltar", icon: <FiArrowLeft size={14} />, onClick: () => navigate("/logistica/entregas") },
    { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
  ];
  if (oe.situacao === "CARREGADA" && podeEntregar) {
    actions.push({
      label: "Registrar entrega", icon: <FiCheckCircle size={14} />,
      onClick: () => setConfirmando("entregar"), primary: true,
    });
    actions.push({
      label: "Registrar não-entrega", icon: <FiXCircle size={14} />,
      onClick: () => setConfirmando("nao_entregar"),
    });
  }

  const kpis = [
    { label: "Pedido", value: oe.pedido_numero, sub: oe.pedido_cliente_nome, tone: "neutral" },
    { label: "Carga", value: oe.carga_numero || "—", tone: "neutral" },
    {
      label: "Situação",
      value: SITUACAO_OE_LABELS[oe.situacao] || oe.situacao,
      tone: SITUACAO_OE_TONES[oe.situacao] || "neutral",
    },
    {
      label: "Assinatura",
      value: assinaturaAConferir
        ? "A conferir"
        : oe.assinada ? "Assinada" : (oe.situacao === "ENTREGUE" ? "Pendente" : "—"),
      tone: assinaturaAConferir ? "warning" : oe.assinada ? "success" : "neutral",
    },
  ];

  return (
    <PageContainer>
      <EntityHeader
        title={oe.numero}
        subtitle={`Entrega de ${oe.pedido_numero}`}
        badges={[{
          label: SITUACAO_OE_LABELS[oe.situacao] || oe.situacao,
          tone: SITUACAO_OE_TONES[oe.situacao] || "neutral",
        }]}
        actions={actions}
      />

      <KpiStrip items={kpis} />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <Card>
        <SectionTitle>Dados</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
          <div><strong>Pedido:</strong> {oe.pedido_numero}</div>
          <div><strong>Cliente:</strong> {oe.pedido_cliente_nome}</div>
          <div><strong>Carga:</strong> {oe.carga_numero || "Sem carga"}</div>
          <div><strong>Endereço:</strong> {oe.endereco_entrega}</div>
          {oe.telefone_contato && <div><strong>Contato:</strong> {oe.telefone_contato}</div>}
          {oe.observacoes_entrega && (
            <div><strong>Observações:</strong> {oe.observacoes_entrega}</div>
          )}
          <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "4px 0" }} />
          <div><strong>Criada em:</strong> {fmtData(oe.created_at)}</div>
          {oe.carregada_em && <div><strong>Carregada em:</strong> {fmtData(oe.carregada_em)}</div>}
          {oe.entregue_em && <div><strong>Entregue em:</strong> {fmtData(oe.entregue_em)}</div>}
          {oe.nao_entregue_em && (
            <>
              <div><strong>Não entregue em:</strong> {fmtData(oe.nao_entregue_em)}</div>
              <div><strong>Motivo:</strong> {oe.motivo_nao_entrega}</div>
            </>
          )}
        </div>
      </Card>

      {oe.situacao === "ENTREGUE" && !oe.assinada && podeEntregar && (
        <Card>
          <SectionTitle>Link de assinatura</SectionTitle>
          <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>
            Envie este link ao cliente para confirmar o recebimento
            digitalmente. Cada cópia fica registrada na auditoria.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {linkPublico && (
              <input
                type="text"
                readOnly
                value={linkPublico}
                aria-label="Link de assinatura"
                style={{
                  flex: 1, padding: "8px 12px",
                  border: "1px solid var(--border)",
                  borderRadius: 6, fontSize: 13,
                  background: "var(--surface-2)",
                  color: "inherit",
                }}
              />
            )}
            <AsyncButton
              variant="secondary"
              onClick={copiarLink}
              successMessage="Link de assinatura copiado."
            >
              <FiCopy size={14} /> Copiar link
            </AsyncButton>
          </div>
          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
            Expira em {oe.assinatura_expira_em ? fmtData(oe.assinatura_expira_em) : "30 dias"}.
          </div>
        </Card>
      )}

      {oe.assinada && (
        <Card>
          <SectionTitle>
            <Pill $tone={assinaturaAConferir ? "warning" : "success"}>
              {assinaturaAConferir ? "Assinada — assinante a conferir" : "Assinada"}
            </Pill>
          </SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
            <div><strong>Assinante:</strong> {oe.assinante_nome}</div>
            {oe.assinante_cpf && <div><strong>CPF:</strong> {oe.assinante_cpf}</div>}
            <div><strong>Assinada em:</strong> {fmtData(oe.assinada_em)}</div>
            {oe.assinante_ip && <div style={{ fontSize: 12, opacity: 0.6 }}><strong>IP:</strong> {oe.assinante_ip}</div>}
            {autorizados.length > 0 && (
              <div>
                <strong>Podiam assinar:</strong>{" "}
                {autorizados
                  .map((a) => `${a.nome}${a.papel ? ` (${a.papel})` : ""}`)
                  .join(" · ")}
              </div>
            )}
          </div>

          {/* CRM-38, decisão do dono: registrar e avisar. A entrega vale; a
              logística confere quem recebeu e a pendência do hub some. */}
          {assinaturaAConferir && (
            <div className="so-banner warning" role="alert" style={{ marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <strong>Quem assinou não está entre os autorizados do cliente.</strong>
                <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>
                  {autorizados.length
                    ? "Confira com o cliente quem recebeu a entrega."
                    : "O cliente não tem contatos autorizados cadastrados — confira quem recebeu."}
                </div>
              </div>
              {podePlanejar && (
                <AsyncButton
                  variant="secondary"
                  silentSuccess
                  onClick={async () => setConfirmando("conferir")}
                >
                  Conferir assinatura
                </AsyncButton>
              )}
            </div>
          )}
          {oe.assinatura_conferida_em && (
            <div style={{ marginTop: 12, fontSize: 13, opacity: 0.8 }}>
              Assinatura conferida por {oe.assinatura_conferida_por_nome || "—"} em{" "}
              {fmtData(oe.assinatura_conferida_em)}
              {oe.assinatura_conferencia_observacao
                ? ` · ${oe.assinatura_conferencia_observacao}`
                : ""}
            </div>
          )}
        </Card>
      )}

      {confirmando && (
        <SlideOverPanel
          open
          onClose={() => { setConfirmando(null); setMotivo(""); setObservacaoConferencia(""); }}
          title={
            confirmando === "entregar" ? "Registrar entrega"
              : confirmando === "conferir" ? "Conferir assinatura"
              : "Registrar não-entrega"
          }
          footer={
            <AsyncButton
              variant={confirmando === "nao_entregar" ? "danger" : "primary"}
              onClick={executar}
              successMessage="Ação executada"
            >
              Confirmar
            </AsyncButton>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {confirmando === "nao_entregar" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Motivo *</span>
                <Textarea
                  rows={3} value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex: Cliente ausente, endereço errado, recusa"
                  maxLength={300}
                />
              </label>
            )}
            {confirmando === "conferir" && (
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Observação (opcional)</span>
                <Textarea
                  rows={3} value={observacaoConferencia}
                  onChange={(e) => setObservacaoConferencia(e.target.value)}
                  placeholder="Ex: falei com a cliente, o filho recebeu no lugar dela"
                  maxLength={300}
                />
              </label>
            )}
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {confirmando === "entregar"
                ? "Marca OE como ENTREGUE + Pedido vai para ENTREGUE em cascata. Gera token para assinatura digital opcional."
                : confirmando === "conferir"
                ? `Registra que a logística conferiu a assinatura de ${oe.assinante_nome || "quem recebeu"} e tira o aviso da Agenda. A entrega não muda de situação.`
                : "Marca OE como NAO_ENTREGUE. Pedido continua EM_EXPEDICAO. Para reagendar, crie nova OE."}
            </div>
          </div>
        </SlideOverPanel>
      )}
    </PageContainer>
  );
}
